import React, { useState, useEffect } from 'react';
import styles from '../capitalExpenditure.module.scss';
import TokenMain from '../../token/TokenMain';

const Settings = ({ onUploadItems, uploading, selectedOffice, selectedFiscalYear }) => {
  const [activeSubTab, setActiveSubTab] = useState('list');
  const [selectedFile, setSelectedFile] = useState(null);
  const [masterItems, setMasterItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    category: ''
  });
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // API Configuration - Auto-detect environment and set appropriate API URL
  const getApiBaseUrl = () => {
    // Check if we're in development (localhost) or production
    const isLocalhost = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1' ||
                       window.location.hostname.includes('localhost');
    
    // Check if we're on the production domain
    const isProduction = window.location.hostname.includes('cloudlab.com.np') ||
                        window.location.hostname.includes('muaccount');
    
    // Priority: localStorage > auto-detection > default
    const savedUrl = localStorage.getItem('apiBaseUrl');
    
    if (savedUrl) {
      return savedUrl;
    }
    
    if (isLocalhost) {
      return 'http://localhost:8000';
    }
    
    if (isProduction) {
      return 'https://muaccount.api.cloudlab.com.np';
    }
    
    // Default fallback
    return 'http://localhost:8000';
  };

  const API_BASE_URL = getApiBaseUrl();

  // Helper function to get authentication headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Token ${token}` })
    };
  };

  const downloadTemplate = () => {
    // Create sample data for the template with proper headers
    const sampleData = [
      ['कोड नं.', 'खर्च शिर्षक', 'Type'],
      ['01.001', 'भवन/भवन निर्माण', 'पुँजीगत खर्च'],
      ['01.002', 'सवारी साधन', 'पुँजीगत खर्च'],
      ['01.003', 'मेसिनरी र उपकरणहरू', 'पुँजीगत खर्च'],
      ['01.004', 'कम्प्युटर र सफ्टवेयर', 'पुँजीगत खर्च'],
      ['01.005', 'फर्निचर र फिक्सचरहरू', 'पुँजीगत खर्च'],
      ['01.006', 'जग्गा/जग्गा दर्ता शुल्क', 'पुँजीगत खर्च'],
      ['02.001', 'कर्मचारी तलब र भत्ता', 'संचालन खर्च'],
      ['02.002', 'सामान्य खर्च', 'संचालन खर्च'],
      ['02.003', 'सेवा शुल्क', 'संचालन खर्च'],
      ['02.004', 'रखरखाव खर्च', 'संचालन खर्च'],
      ['02.005', 'कागजात र सामग्री', 'संचालन खर्च'],
      ['02.006', 'यातायात खर्च', 'संचालन खर्च'],
      ['02.007', 'दूरसञ्चार खर्च', 'संचालन खर्च'],
      ['02.008', 'बिजुली र पानी', 'संचालन खर्च']
    ];
    
    // Since we can't create Excel files directly in the browser without additional libraries,
    // let's create a proper CSV file that Excel can open and save as Excel
    const csvContent = sampleData.map(row => 
      row.map(cell => `"${cell}"`).join(',')
    ).join('\n');
    
    // Add BOM for proper UTF-8 encoding in Excel
    const BOM = '\uFEFF';
    const csvWithBOM = BOM + csvContent;
    
    // Create and download as CSV file (Excel can open this and save as .xlsx)
    const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'expenditure_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Show user instruction
    setTimeout(() => {
      alert('टेम्पलेट डाउनलोड भयो! कृपया:\n\n1. फाइल खोल्नुहोस् (Excel मा खुल्नेछ)\n2. डाटा भर्नुहोस्\n3. "फाइल" → "नयाँ रूपमा सेभ गर्नुहोस्" → "Excel कार्यपुस्तिका (.xlsx)" छान्नुहोस्\n4. फिर्ता अपलोड गर्नुहोस्');
    }, 100);
  };

  const loadMasterItems = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/master-expenditure-items/`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success') {
          setMasterItems(result.data);
        }
      }
    } catch (error) {
      console.error('Error loading master items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditItem = (item) => {
    setEditingId(item.id);
    setFormData({
      name: item.expense_title,
      code: item.code_number,
      category: item.item_type
    });
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/master-expenditure-items/${editingId}/update/`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success') {
          // Update local state
          setMasterItems(prevItems => 
            prevItems.map(item => 
              item.id === editingId ? result.data : item
            )
          );
          setEditingId(null);
          setFormData({ name: '', code: '', category: '' });
          alert('सूची सफलतापूर्वक अपडेट गरियो।');
        }
      } else {
        alert('अपडेट असफल। कृपया पुनः प्रयास गर्नुहोस्।');
      }
    } catch (error) {
      console.error('Error updating item:', error);
      alert('अपडेट असफल। कृपया पुनः प्रयास गर्नुहोस्।');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setFormData({ name: '', code: '', category: '' });
  };

  // Handle select all checkbox
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedItems(masterItems.map(item => item.id));
    } else {
      setSelectedItems([]);
    }
  };

  // Handle individual item selection
  const handleItemSelect = (itemId, checked) => {
    if (checked) {
      setSelectedItems(prev => [...prev, itemId]);
    } else {
      setSelectedItems(prev => prev.filter(id => id !== itemId));
    }
  };

  // Bulk delete function
  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) {
      alert('कृपया मेटाउन चाहने आइटमहरू छान्नुहोस्');
      return;
    }
    
    const confirmDelete = window.confirm(`तपाईंले ${selectedItems.length} वटा आइटमहरू मेटाउन चाहनुहुन्छ? यो कार्य फिर्ता गर्न सकिँदैन।`);
    
    if (confirmDelete) {
      try {
        // Call the delete function for each selected item
        for (const itemId of selectedItems) {
          const itemToDelete = masterItems.find(item => item.id === itemId);
          if (itemToDelete) {
            await handleDeleteItem(itemToDelete);
          }
        }
        
        // Clear selection after deletion
        setSelectedItems([]);
        setSelectAll(false);
        
        alert(`${selectedItems.length} वटा आइटमहरू सफलतापूर्वक मेटाइयो`);
      } catch (error) {
        console.error('Bulk delete error:', error);
        alert('केही आइटमहरू मेटाउन असफल। कृपया पुनः प्रयास गर्नुहोस्।');
      }
    }
  };

  const handleDeleteItem = async (item) => {
    if (window.confirm(`के तपाईं यो सूची मेटाउन चाहनुहुन्छ?\nकोड: ${item.code_number}\nशिर्षक: ${item.expense_title}`)) {
      try {
        const response = await fetch(`${API_BASE_URL}/master-expenditure-items/${item.id}/delete/`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });
        
        if (response.ok) {
          // Remove item from local state
          setMasterItems(prevItems => prevItems.filter(i => i.id !== item.id));
          alert('सूची सफलतापूर्वक मेटाइयो।');
        } else {
          alert('मेटाउन असफल। कृपया पुनः प्रयास गर्नुहोस्।');
        }
      } catch (error) {
        console.error('Error deleting item:', error);
        alert('मेटाउन असफल। कृपया पुनः प्रयास गर्नुहोस्।');
      }
    }
  };

  useEffect(() => {
    loadMasterItems();
  }, []);

  // Reset selection when masterItems change
  useEffect(() => {
    setSelectedItems([]);
    setSelectAll(false);
  }, [masterItems]);

  return (
    <div className={styles.settingsContainer}>
      <h2 className={styles.settingsTitle}>सेटिङहरू</h2>
      
      {/* Sub-tab Navigation */}
      <div className={styles.subTabNavigation}>
        <button
          className={`${styles.subTabButton} ${activeSubTab === 'list' ? styles.activeSubTab : ''}`}
          onClick={() => setActiveSubTab('list')}
        >
          सूची
        </button>
        <button
          className={`${styles.subTabButton} ${activeSubTab === 'token' ? styles.activeSubTab : ''}`}
          onClick={() => setActiveSubTab('token')}
        >
          टोकन
        </button>
        {/* Add more sub-tabs here in the future */}
      </div>

             {/* Content based on active sub-tab */}
       {activeSubTab === 'list' && (
         <div className={styles.listSettingsContent}>

            
                        <div className={styles.uploadSection}>
              
                             <label className={styles.uploadLabel}>सूची अपलोड गर्नुहोस् (Excel .xlsx बढी राम्रो):</label>
            <div className={styles.uploadInputContainer}>
              <input
                type="file"
                id="settings-file-upload"
                accept=".xlsx,.xls,.csv"
                className={styles.uploadInput}
                onChange={async (e) => {
                  const file = e.target.files && e.target.files[0];
                  if (file) {
                    setSelectedFile(file);
                    if (onUploadItems) {
                      await onUploadItems(file);
                      // Refresh master items after upload
                      setTimeout(() => loadMasterItems(), 1000);
                    }
                  }
                }}
                disabled={uploading}
                title="फाइल छान्नुहोस्"
              />
              <button
                type="button"
                className={styles.uploadButton}
                onClick={() => {
                  document.getElementById('settings-file-upload').click();
                }}
                disabled={uploading}
                title="फाइल छान्नुहोस्"
              >
                {uploading ? 'अपलोड हुँदैछ...' : 'फाइल छान्नुहोस्'}
              </button>
              

            </div>
            
            {selectedFile && (
              <div className={styles.selectedFileInfo}>
                <strong>छानिएको फाइल:</strong> {selectedFile.name}
                <button
                  type="button"
                  className={styles.clearFileButton}
                  onClick={() => {
                    setSelectedFile(null);
                    document.getElementById('settings-file-upload').value = '';
                  }}
                >
                  ×
                </button>
              </div>
            )}
            
            {uploading && <span className={styles.uploadingNote}>अपलोड हुँदैछ...</span>}
            
                         <div className={styles.uploadHint}>
               <strong>स्तम्भहरू:</strong> "कोड नं.", "खर्च शिर्षक", "Type" (पुँजीगत खर्च/संचालन खर्च)
               <br />
               <small><strong>बढी राम्रो:</strong> Excel (.xlsx) फाइलहरू प्रयोग गर्नुहोस् - CSV पनि स्वीकार गरिन्छ</small>
               <br />
               <strong>महत्वपूर्ण:</strong> फाइलमा कुनै खाली पङ्क्तिहरू नहुन् र सबै आवश्यक स्तम्भहरू भर्नुहोस्
               <br />
               <button 
                 type="button"
                 onClick={downloadTemplate}
                 style={{ 
                   marginTop: '10px',
                   padding: '8px 16px',
                   background: '#28a745',
                   color: 'white',
                   border: 'none',
                   borderRadius: '6px',
                   cursor: 'pointer',
                   fontSize: '14px',
                   fontWeight: 'bold'
                 }}
               >
                 📥 CSV टेम्पलेट डाउनलोड गर्नुहोस्
               </button>
               <br />
               <small style={{ color: '#28a745', fontWeight: 'bold' }}>
                 💡 टिप: CSV टेम्पलेट डाउनलोड गरेर डाटा भर्नुहोस्, त्यसपछि Excel मा खोलेर .xlsx फरमाटमा सेभ गर्नुहोस्
               </small>
             </div>
          </div>

                     {/* Master Items Display */}
           <div className={styles.masterItemsSection}>
             <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
               <h3>अपलोड गरिएका सूचीहरू ({masterItems.length})</h3>
               <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                 <button 
                   onClick={loadMasterItems} 
                   disabled={loading}
                   style={{ 
                     padding: '8px 16px', 
                     background: '#007bff', 
                     color: 'white', 
                     border: 'none', 
                     borderRadius: '4px', 
                     cursor: 'pointer' 
                   }}
                 >
                   {loading ? 'लोड हुँदैछ...' : 'रिफ्रेश'}
                 </button>
                 
                 {/* Bulk Delete Button */}
                 {selectedItems.length > 0 && (
                   <button 
                     onClick={handleBulkDelete}
                     style={{ 
                       padding: '8px 16px', 
                       background: '#dc3545', 
                       color: 'white', 
                       border: 'none', 
                       borderRadius: '4px', 
                       cursor: 'pointer',
                       fontWeight: 'bold'
                     }}
                     title={`${selectedItems.length} वटा आइटमहरू मेटाउनुहोस्`}
                   >
                     🗑️ बल्क डिलिट ({selectedItems.length})
                   </button>
                 )}
               </div>
             </div>
             {loading ? (
               <p>लोड हुँदैछ...</p>
             ) : masterItems.length > 0 ? (
               <div className={styles.masterItemsTable}>
                 {/* Table Header */}
                 <div className={styles.masterItemsHeader}>
                   <div className={styles.headerCheckbox}>
                     <input
                       type="checkbox"
                       checked={selectAll}
                       onChange={(e) => handleSelectAll(e.target.checked)}
                       title="सबै छान्नुहोस्"
                     />
                   </div>
                   <div className={styles.headerCode}>कोड नं.</div>
                   <div className={styles.headerTitle}>खर्च शिर्षक</div>
                   <div className={styles.headerType}>Type</div>
                   <div className={styles.headerActions}>कार्यहरू</div>
                 </div>
                 {/* Table Body */}
                 <div className={styles.masterItemsList}>
                   {masterItems.map((item) => (
                     <div key={item.id} className={styles.masterItem}>
                       <div className={styles.itemCheckbox}>
                         <input
                           type="checkbox"
                           checked={selectedItems.includes(item.id)}
                           onChange={(e) => handleItemSelect(item.id, e.target.checked)}
                           title="यो आइटम छान्नुहोस्"
                         />
                       </div>
                       <div className={styles.itemCode}>{item.code_number}</div>
                       <div className={styles.itemTitle}>{item.expense_title}</div>
                       <div className={`${styles.itemType} ${styles[item.item_type]}`}>
                         {item.item_type === 'capital' ? 'पुँजीगत खर्च' : 'संचालन खर्च'}
                       </div>
                       <div className={styles.itemActions}>
                         <button 
                           className={styles.editButton}
                           onClick={() => handleEditItem(item)}
                           title="सम्पादन गर्नुहोस्"
                         >
                           ✏️
                         </button>
                         <button 
                           className={styles.deleteButton}
                           onClick={() => handleDeleteItem(item)}
                           title="मेटाउनुहोस्"
                         >
                           🗑️
                         </button>
                       </div>
                     </div>
                   ))}
                 </div>
               </div>
             ) : (
               <p>अहिलेसम्म कुनै सूची अपलोड गरिएको छैन।</p>
                          )}
           </div>

           {/* Edit Modal */}
           {editingId && (
             <div className={styles.editModal}>
               <div className={styles.editModalContent}>
                 <h3>सूची सम्पादन गर्नुहोस्</h3>
                 <div className={styles.editForm}>
                   <div className={styles.formGroup}>
                     <label>कोड नं.:</label>
                     <input
                       type="text"
                       value={formData.code}
                       onChange={(e) => setFormData({...formData, code: e.target.value})}
                       placeholder="कोड नं. लेख्नुहोस्"
                     />
                   </div>
                   <div className={styles.formGroup}>
                     <label>खर्च शिर्षक:</label>
                     <input
                       type="text"
                       value={formData.name}
                       onChange={(e) => setFormData({...formData, name: e.target.value})}
                       placeholder="खर्च शिर्षक लेख्नुहोस्"
                     />
                   </div>
                   <div className={styles.formGroup}>
                     <label>Type:</label>
                     <select
                       value={formData.category}
                       onChange={(e) => setFormData({...formData, category: e.target.value})}
                     >
                       <option value="capital">पुँजीगत खर्च</option>
                       <option value="operational">संचालन खर्च</option>
                     </select>
                   </div>
                   <div className={styles.editActions}>
                     <button onClick={handleSaveEdit} className={styles.saveButton}>
                       सेभ गर्नुहोस्
                     </button>
                     <button onClick={handleCancelEdit} className={styles.cancelButton}>
                       रद्द गर्नुहोस्
                     </button>
                   </div>
                 </div>
               </div>
             </div>
           )}
         </div>
       )}

       {/* Token Tab Content */}
       {activeSubTab === 'token' && (
         <div className={styles.tokenSettingsContent}>
           <TokenMain isAuthenticated={!!localStorage.getItem('authToken')} />
         </div>
       )}
     </div>
   );
 };

export default Settings;
